import { useCallback, useEffect, useState } from 'react';
import {
  getSubscriptionRevenueData,
  Request,
  Response,
} from 'services/api/getSubscriptionRevenueData';
import { FetchState } from 'types';

/**
 * SubscriptionRevenueData extends Response with date as Date instead of Timestamp
 */
type SubscriptionRevenueData = Omit<Response[number], 'date'> & { date: Date };

type UseSubscriptionRevenueDataReturn = {
  data: SubscriptionRevenueData[];
  state: FetchState;
  error: Error | null;
  refetch: () => void;
};

export const useSubscriptionRevenueData = <T extends Request['endpoint']>(
  memoizedRequest: Request<T>,
): UseSubscriptionRevenueDataReturn => {
  const [state, setState] = useState<FetchState>('loading');
  const [data, setData] = useState<UseSubscriptionRevenueDataReturn['data']>([]);
  const [error, setError] = useState<Error | null>(null);
  const [refetchKey, setRefetchKey] = useState(0);

  const refetch = useCallback(() => {
    setRefetchKey((prev) => prev + 1);
  }, []);

  useEffect(
    function fetchSubscriptionRevenueData() {
      try {
        setState('loading');

        getSubscriptionRevenueData(memoizedRequest)
          .then((result) => {
            setData(result.data.map((item) => ({ ...item, date: new Date(item.date) })));
            setState('success');
          })
          .catch((error) => {
            console.log(error);
            setError(
              error instanceof Error
                ? error
                : new Error('Failed to fetch subscription revenue data'),
            );

            setState('error');
          });
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to fetch subscription revenue data'),
        );

        console.log('error', err);
        setState('error');
      }
    },
    [memoizedRequest, refetchKey],
  );

  return {
    data,
    state,
    error,
    refetch,
  };
};
